/**
 * User Search Cache - Optimizes Firebase user search by caching results
 *
 * Features:
 * - LRU cache with TTL for search results
 * - Prefix matching for instant suggestions
 * - Reduces redundant Firestore queries
 */

import { UserProfile } from '@watchlist/shared';

interface CacheEntry {
  users: UserProfile[];
  timestamp: number;
  query: string;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_ENTRIES = 50;

class UserSearchCache {
  private cache: Map<string, CacheEntry> = new Map();
  private accessOrder: string[] = [];

  /**
   * Normalize query for cache key
   */
  private normalizeQuery(query: string): string {
    return query.toLowerCase().trim();
  }

  /**
   * Get cached results for a query
   */
  get(query: string): UserProfile[] | null {
    const normalizedQuery = this.normalizeQuery(query);
    const entry = this.cache.get(normalizedQuery);

    if (!entry) {
      return null;
    }

    // Check TTL
    if (Date.now() - entry.timestamp > CACHE_TTL) {
      this.cache.delete(normalizedQuery);
      return null;
    }

    // Update access order for LRU
    this.updateAccessOrder(normalizedQuery);
    console.log('👤 UserSearchCache: Cache hit for:', normalizedQuery);
    return entry.users;
  }

  /**
   * Try to find results from a prefix query cache
   * e.g., if "john" is cached, use it for "john doe"
   */
  getFromPrefix(query: string): UserProfile[] | null {
    const normalizedQuery = this.normalizeQuery(query);

    // Find the longest prefix that has cached results
    for (let i = normalizedQuery.length - 1; i >= 2; i--) {
      const prefix = normalizedQuery.substring(0, i);
      const entry = this.cache.get(prefix);

      if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
        // Filter cached results by the full query
        const filteredResults = entry.users.filter(user => {
          const displayName = (user.displayName || '').toLowerCase();
          const username = (user.username || '').toLowerCase();
          const email = (user.email || '').toLowerCase();

          return (
            displayName.includes(normalizedQuery) ||
            username.includes(normalizedQuery) ||
            email.includes(normalizedQuery)
          );
        });

        if (filteredResults.length > 0) {
          console.log('👤 UserSearchCache: Prefix cache hit:', prefix, '→', normalizedQuery);
          return filteredResults;
        }
      }
    }

    return null;
  }

  /**
   * Store results in cache
   */
  set(query: string, users: UserProfile[]): void {
    const normalizedQuery = this.normalizeQuery(query);

    // Enforce max cache size using LRU
    if (this.cache.size >= MAX_CACHE_ENTRIES) {
      const oldestKey = this.accessOrder.shift();
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(normalizedQuery, {
      users,
      timestamp: Date.now(),
      query: normalizedQuery
    });

    this.updateAccessOrder(normalizedQuery);
    console.log('👤 UserSearchCache: Cached', users.length, 'users for:', normalizedQuery);
  }

  /**
   * Update access order for LRU eviction
   */
  private updateAccessOrder(key: string): void {
    const index = this.accessOrder.indexOf(key);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
    }
    this.accessOrder.push(key);
  }

  /**
   * Clear entire cache
   */
  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; entries: string[] } {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys())
    };
  }
}

// Export singleton instance
export const userSearchCache = new UserSearchCache();
